// What the signed-in user may do in ShortCircuit. The source of truth is the
// user's app role in the staffing-app (Settings > App Roles), module
// 'shortcircuit' { read, create, update, delete }. The API enforces the same
// rules in the Authorizer (SHORTCIRCUIT_ROUTES); this only decides what the UI
// shows, so keep the two in step:
//   read    view (any of the four verbs: write or delete imply read)
//   write   create OR update: new and edited projects and designs, uploads,
//           sharing, generation, upscaling, batch jobs (the API has one write
//           tier, so the UI uses canWrite for all of them)
//   delete  delete projects, designs, masks, batch jobs
// The super admin can do everything, survey admin included.

export const SUPER_ADMIN_ROLE_ID = 'role-superadmin'

export const isSuperAdminRole = (appRoleId) =>
  String(appRoleId || '').toLowerCase() === SUPER_ADMIN_ROLE_ID

export const NO_ACCESS = Object.freeze({
  isSuperAdmin: false,
  canRead: false,
  canCreate: false,
  canUpdate: false,
  canWrite: false,
  canDelete: false,
})

// permissions: the role's permissions JSON ({ shortcircuit: {...}, ... }) or null.
export const computeScAccess = (permissions, appRoleId) => {
  if (isSuperAdminRole(appRoleId)) {
    return {
      isSuperAdmin: true,
      canRead: true,
      canCreate: true,
      canUpdate: true,
      canWrite: true,
      canDelete: true,
    }
  }
  const sc = (permissions && typeof permissions === 'object' && permissions.shortcircuit) || {}
  const canCreate = !!sc.create
  const canUpdate = !!sc.update
  const canDelete = !!sc.delete
  return {
    isSuperAdmin: false,
    canRead: !!sc.read || canCreate || canUpdate || canDelete,
    canCreate,
    canUpdate,
    // The API has one write tier for create and update (project:write, design:write).
    canWrite: canCreate || canUpdate,
    canDelete,
  }
}

// GET /user?email= answers { data: [record] }, [record] or the record itself.
export const pickUserRecord = (payload) => {
  if (Array.isArray(payload)) return payload[0] || null
  if (payload && typeof payload === 'object') {
    if (Array.isArray(payload.data)) return payload.data[0] || null
    if (payload.data && typeof payload.data === 'object') return payload.data
    if ('permissions' in payload || 'email' in payload) return payload
  }
  return null
}

// Raised by core/api.jsx when the API refuses a request for lack of permission,
// so the permissions are re-read (the role may have changed in Settings).
export const FORBIDDEN_EVENT = 'sc:forbidden'
